<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import BlogItem from '@/components/BlogItem.vue'
import BotFooter from '@/components/BotFooter.vue'
import SocialButtons from '@/components/SocialButtons.vue'
import TopNav from '@/components/TopNav.vue'
import { blog as blogs } from '@/data/blog'
import { blogCategories } from '@/data/blogCategories'
import { langFromPath, localizer, useLangTools } from '@/lang'
import { baseUrl } from '@/utils/config'
import { useSpeech } from '@/utils/speechSynthesis'
import { useHead } from '@unhead/vue'
import { onServerPrefetch, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// import CatalogSlider from '@/components/CatalogSlider.vue';
// import ValuateDiamond from './ValuateDiamond.vue';

const speech = useSpeech()

const route = useRoute()
const router = useRouter()
const { $tdb } = useLangTools(router)

let currentBlog: Record<string, any> | undefined = _ref()
const lang = _computed(() => langFromPath(route.path))

function setSchema() {
	const link = [{
		rel: 'canonical',
		href: `${baseUrl}${localizer(currentBlog.value?.url, lang.value)}`,
	}]
	const meta = [
		{ name: 'title', content: $tdb(currentBlog.value, 'metaTitle', lang.value) || currentBlog.value?.title },
		{ name: 'description', content: $tdb(currentBlog.value, 'metaDescription', lang.value) },
		{ name: 'og:image', content: currentBlog.value?.ogImage },
		{ name: 'og:title', content: $tdb(currentBlog.value, 'metaTitle', lang.value) },
		{ name: 'og:description', content: $tdb(currentBlog.value, 'metaDescription', lang.value) },
		{ name: 'twitter:card', content: 'summary' },
		{ name: 'twitter:title', content: $tdb(currentBlog.value, 'metaTitle', lang.value) },
		{ name: 'twitter:description', content: $tdb(currentBlog.value, 'metaDescription', lang.value) },
		{ name: 'twitter:image', content: currentBlog.value?.ogImage },
	]
	useHead({
		link,
		meta,
		title: $tdb(currentBlog.value, 'metaTitle', lang.value) || currentBlog.value?.title,
		script: [
			{
				type: 'application/ld+json',
				innerHTML: `
				{
  				"@context": "https://schema.org",
					"@type": "Article",
					"headline": "${currentBlog.value?.title}",
					"description": "${currentBlog.value?.metaDescription}",
					"image": "${currentBlog.value?.image?.imageURL}",
					"datePublished": "${currentBlog.value?.publishDate || currentBlog.value?._createdDate}",
					"publisher": {
						"@type": "Organization",
						"name": "Auctentic",
						"logo": {
							"@type": "ImageObject",
							"url": "${baseUrl}/logo.png"
						}
					},
					"mainEntityOfPage": {
						"@type": "WebPage",
						"@id": "${baseUrl}/${currentBlog.value?.url}"
					}
				}
  				`,
			},
		],
	})
}

let otherBlogs: Record<string, any>[] = _ref([])
const blogCategory = _computed(() => blogCategories.find(bc => bc._id === currentBlog.value?.category?.[0]?.itemRefID))
async function fetch() {
	currentBlog.value = blogs.find(b => b.slug === route.params.article_slug)
	const others = blogs.filter(
		b => b.language.value === currentBlog.value?.language?.value && b._id !== currentBlog.value._id,
	)
	const randomNum = Math.floor(Math.random() * (others.length - 1 + 1)) + 1
	otherBlogs.value = others.slice(randomNum, randomNum + 3)
	setSchema()
}

watch(route, fetch, { immediate: true })
onServerPrefetch(fetch)

const dateCategoryString = _computed(() => {
	const dateStr = new Date(currentBlog.value?._createdDate || '').toLocaleDateString()
	const categoryStr = blogCategory.value ? $tdb(blogCategory.value, 'name') : ''
	// const author = currentBlog?.author;
	return [dateStr, categoryStr].filter(f => !!f).join('|')
})
const showAudio = _ref(speech.isSupported)

watch(
	() => currentBlog.value,
	(val) => {
		if (!val) router.replace(localizer('/blog')).catch(console.error)
	},
)

let audioStatus = _ref<'playing' | 'paused' | 'stopped'>('stopped')

function pauseText() {
	speech.pause()
	audioStatus.value = 'paused'
}

function speakText() {
	if (audioStatus.value === 'paused') {
		speech.resume()
	} else { speak()
	}
	audioStatus.value = 'playing'
}

function speak() {
	const article = document.querySelector('.article')?.textContent?.split('\n').join('.\n') || ''
	const text = `
		${currentBlog.value?.title}.
		${currentBlog.value?.description}
		${article}
		`
	speech.speak(text)
}

onUnmounted(() => speech.cancel())

const opentoc = _ref(true)
</script>

<template>
	<div>
		<div class="act_oval" />
		<TopNav />
		<div class="page-wrap">
			<div class="white-bg">
				<div class="article-box act_w900">
					<div class="breadcrumbs act_txt14 act_flex act_gap-1 act_pb-2">
						<LocalLink :title="$t('general.blog')" class="decoration-none hover act_black" to="/blog">
							{{ $t('general.blog') }}
						</LocalLink>
						<div class="breadcrumbs-line" />

						<template v-if="blogCategory">
							<LocalLink
								:title="$tdb(blogCategory, 'name')"
								class="decoration-none hover act_black"
								:to="`/blog/${$tdb(blogCategory, 'slug')}`"
							>
								{{ $tdb(blogCategory, 'name') }}
							</LocalLink>
							<div class="breadcrumbs-line" />
						</template>

						<p class="decoration-none act_black ellipsis-1">
							{{ currentBlog?.title }}
						</p>
					</div>
					<h1 class="playfair act_txt60 act_no-marg act_pb-1 balance">
						{{ currentBlog?.title }}
					</h1>
					<p class="act_txt14 act_pb-2">
						{{ dateCategoryString }}
					</p>
					<p class="act_pb-2">
						{{ currentBlog?.description }}
					</p>
					<div
						v-if="showAudio && audioStatus !== 'playing'"
						class="hover act_flex act_gap-05 align-center act_txt14 act_mb-2 justify-content-center"
						@click="speakText()"
					>
						<img width="39" height="39" src="@/assets/Listen.svg" alt="Listen">
						<p>Listen</p>
					</div>
					<div
						v-if="audioStatus === 'playing'"
						class="hover act_flex act_gap-05 align-center act_txt14 act_mb-2 justify-content-center"
						@click="pauseText()"
					>
						<img width="39" height="39" src="@/assets/Listen.svg" alt="Listen">
						<p>Audio playing</p>
					</div>
					<div class="article-main-img act_mb-4">
						<img
							v-if="currentBlog?.image?.imageURL"
							width="1170"
							height="600"
							:src="currentBlog?.image?.imageURL"
							:alt="currentBlog?.image.altText || currentBlog?.title"
						>
					</div>
					<div class="act_pb-2">
						<div class="act_flex hover act_gap-05 align-center" @click="opentoc = !opentoc">
							<div class="toc-icon" :class="{ opentoc }">
								<img class="toc-plus-min" src="@/assets/toc-min.svg" alt="Table of Contents">
								<img class="toc-plus" src="@/assets/toc-plus.svg" alt="Table of Contents">
							</div>
							<p class="playfair act_txt24">
								{{ $t('general.tableOfContents') }}
							</p>
						</div>
						<div class="toc-list" :class="{ opentoc }">
							<div
								v-for="item in currentBlog?.contentTable"
								:key="item.id"
								class="hover act_pt-025 act_pb-025"
							>
								<RouterLink
									:to="`#${item.id}`"
								>
									{{ item.innerText }}
								</RouterLink>
							</div>
						</div>
					</div>
					<div class="article" v-html="currentBlog?.body" />
					<div class="act_pt-4 flex-desktop toc justify-content-space-between">
						<SocialButtons :title="currentBlog?.title" />
					</div>
				</div>
			</div>

			<div class="act_w1170">
				<p class="act_txt40 playfair act_center act_pb-3 act_pt-2">
					{{ $t('general.youMayAlsoLike') }}
				</p>
				<div class="blogs-list grid-wrap act_gap-2">
					<BlogItem v-for="blog in otherBlogs" :key="blog._id" :blog="blog" />
				</div>
			</div>
		</div>
		<BotFooter />
	</div>
</template>

<style>
.align-left{
	float: left;
}
.align-right{
	float: right;
}
.article {
	text-align: start;
	line-height: 2;
}

.article p {
	margin-bottom: 10px;
}

.article h2,
.article h3,
.article h4 {
	margin-bottom: 10px;
	font-size: 30px;
	font-weight: 400;
	line-height: 1.3;
}

.article img {
	border-radius: 20px;
	margin-bottom: 10px;
}
.article th{
	background: var(--blue-dark);
	color: white;
	text-align: start;
	border-color:transparent;
}
.article table{
	border-color: #0b233535;
	margin-block: 1rem;
}

.article td, .article th {
	padding: 0.75rem 0.5rem;
	line-height: 1.3;
}

.article table p, .article table span {
	margin: 0 !important;
	display: block;
	line-height: 1.3;

}

.article-main-img {
	width: calc(100% + 366px);
	margin-left: -183px;
	margin-right: -183px;
	display: block;
}

.article-main-img img {
	width: 100%;
	max-height: 600px;
	object-fit: cover;
	border-radius: 20px;
}

.toc-list {
	max-height: 0;
	overflow: hidden;
	text-align: start;
	transition: all 400ms ease;
	padding-inline-start: 2rem;
}

.toc-list.opentoc {
	max-height: 100vh;
	transition: all 400ms ease;
}

.toc-list a {
	text-decoration: none;
	padding-top: 0.5rem;
	font-size: 16px;
	border-top: 1px solid var(--blue);
	color: var(--gray);
}

.toc-icon {
	position: relative;
	height: 20px;
	width: 20px;
}

.toc-icon img {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
}

.opentoc .toc-plus {
	opacity: 0;
}

.toc-plus {
	transition: all 400ms ease;
}

.white-bg {
	background: white;
}

.article-box {
	padding: 3rem;
	color: var(--black);
}

.article-box .act_txt60 {
	font-size: 50px;
}

.hide-basic-page .oval,
.hide-basic-page .topnav,
.hide-basic-page .hiddenNav,
.hide-basic-page .menu,
.hide-basic-page .scroll-up.show,
.hide-basic-page .footer-wrap {
	display: none;
}

@media screen and (max-width: 1180px) {
	.article-main-img {
		width: calc(100% + 200px);
		margin-left: -100px;
		margin-right: -100px;
	}
}

@media screen and (max-width: 1020px) {
	.stickFirstColTable tr td:first-child {
	position: sticky;
	inset-inline-start: 0;
	background: #d8dfe4;
}
	.article-main-img {
		width: 100%;
		margin-left: 0;
		margin-right: 0;
	}

	.article-box {
		padding: 1.5rem 0.5rem;
	}

	.article-box .act_txt60 {
		font-size: 30px;
	}

	.article h2,
	.article h3,
	.article h4 {
		font-size: 26px;
		line-height: 1.4;
	}

	.article-main-img img {
		max-height: 300px;
	}
	.article table{
		display: block;
		height: 100% !important;
		overflow: auto;
}
}
</style>
